// Copying text, done once for the whole app.
//
// This was written three separate times — the share sheet on an event, the
// invite code of a shared calendar, the calendar subscription link — and each
// copy got it wrong in its own way:
//   • `navigator.clipboard?.writeText(x).then(…)` evaluates to nothing at all
//     when the Clipboard API is missing, so the success tick never ran and the
//     failure never surfaced. Android WebViews do exactly that in some setups.
//   • a synchronous try/catch around writeText catches nothing: it returns a
//     promise, so a rejection sails straight past — and the code went on to
//     announce "Copied ✓" for a copy that had not happened.
//
// One place, one behaviour: it either copied or it didn't, and it says which.

/**
 * @param {string} text
 * @returns {Promise<boolean>} true only if the text really reached the clipboard
 */
export async function copyText(text) {
  if (text == null) return false;
  const s = String(text);

  if (navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(s);
      return true;
    } catch (_) {
      // Blocked (no permission, not a secure context, WebView quirk) — try the
      // old way rather than give up.
    }
  }

  // execCommand is deprecated and still the only thing that works where the
  // Clipboard API is unavailable. Ugly, and better than a silent failure.
  try {
    const ta = document.createElement('textarea');
    ta.value = s;
    ta.setAttribute('readonly', '');
    ta.style.position = 'fixed';
    ta.style.top = '0';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.select();
    ta.setSelectionRange(0, s.length);   // iOS needs the explicit range
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    return !!ok;
  } catch (_) {
    return false;
  }
}
